
import { VehicleInfo, VehicleExtractionResult } from './types';

// Re-export types for other modules to use
export type { VehicleInfo, VehicleExtractionResult };

export const extractVehicleInfo = (text: string): VehicleExtractionResult => {
  const startTime = performance.now();
  const logs: any[] = [];
  
  const log = (message: string, data?: any) => {
    const entry = {
      timestamp: new Date().toISOString(),
      step: 'vehicle_extraction',
      message,
      ...(data && { data })
    };
    logs.push(entry);
    console.log(`[Vehicle Extraction] ${message}`, data || '');
  };

  log('Starting vehicle info extraction');
  
  if (!text) {
    throw new Error('No text provided for VIN extraction');
  }

  // Clean and normalize the text
  let cleanText = text
    .replace(/\n+/g, ' ')           
    .replace(/\s+/g, ' ')           
    .replace(/[^\x20-\x7E]/g, ' ')  
    .trim();
    
  log('Text cleaned and normalized');

  // VIN extraction patterns - ordered from most specific to least specific
  const vinPatterns = [
    /VIN:\s*([A-HJ-NPR-Z0-9]{17})/i,
    /VIN\s*#?\s*([A-HJ-NPR-Z0-9]{17})/i,
    /Vehicle\s+ID\s*:?\s*([A-HJ-NPR-Z0-9]{17})/i,
    /Vehicle\s+Identification\s+Number:?\s*([A-HJ-NPR-Z0-9]{17})/i,
    /([A-HJ-NPR-Z0-9]{17})/g  // General pattern as last resort
  ];

  let vin = '';
  let vinSource = '';

  // First try the specific patterns
  for (const pattern of vinPatterns.slice(0, -1)) {
    const match = cleanText.match(pattern);
    if (match && match[1]) {
      const potentialVin = match[1].toUpperCase();
      if (!/^0+$/.test(potentialVin) && !/(.)\1{10,}/.test(potentialVin)) {
        vin = potentialVin;
        vinSource = pattern.toString();
        log('Found VIN using specific pattern', { vin, pattern: pattern.toString() });
        break;
      }
    }
  }

  // If no VIN found with specific patterns, try the general pattern
  if (!vin) {
    const matches = cleanText.match(vinPatterns[vinPatterns.length - 1]);
    if (matches) {
      for (const match of matches) {
        if (/^[A-HJ-NPR-Z0-9]{17}$/.test(match) && 
            !/^0+$/.test(match) && 
            !/(.)\1{10,}/.test(match)) {
          vin = match.toUpperCase();
          vinSource = 'global_pattern';
          log('Found VIN using global pattern', { vin });
          break;
        }
      }
    }
  }

  // Extract RO number with variations
  const roPatterns = [
    /RO\s*(?:Number|#|No\.?)?\s*[:.]?\s*(\d{6,12})/i,
    /Repair\s*Order\s*(?:Number|#|No\.?)?\s*[:.]?\s*(\d{6,12})/i,
    /(?:^|\s)(?:R\.?O\.?\s*[:.]?\s*)(\d{6,12})(?:\s|$)/i
  ];

  let roNumber = '';
  for (const pattern of roPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      roNumber = match[1];
      log('Found RO Number', { roNumber, pattern: pattern.toString() });
      break;
    }
  }

  // Extract year, make, model
  const yearPattern = /\b(19|20)\d{2}\b/;
  const yearMatch = cleanText.match(yearPattern);
  const year = yearMatch ? yearMatch[0] : '';

  // Look for make and model after the year
  let make = '';
  let model = '';
  if (year) {
    const afterYear = cleanText.substring(cleanText.indexOf(year) + 4);
    const makeModelPattern = /([A-Za-z]+)\s+([A-Za-z0-9]+(?:\s+[A-Za-z0-9]+)*)/;
    const makeModelMatch = afterYear.match(makeModelPattern);
    if (makeModelMatch) {
      make = makeModelMatch[1].trim();
      model = makeModelMatch[2].trim();
    }
  }

  // Extract other vehicle information
  const patterns = {
    exteriorColor: /(?:Exterior|Ext\.?)\s*Color:\s*([A-Za-z]+)/i,
    interiorColor: /(?:Interior|Int\.?)\s*Color:\s*([A-Za-z]+)/i,
    mileageIn: /(?:Mileage|Miles|ODO)(?:\s+In)?:\s*([\d,]+)/i,
    mileageOut: /Mileage\s+Out:\s*([\d,]+)/i,
    license: /License(?:\s+(?:Plate|Number))?:\s*([A-Z0-9]+)/i,
    state: /State:\s*([A-Z]{2})/i,
    productionDate: /Production\s+Date:\s*(\d{1,2}\/\d{4})/i,
    condition: /Condition:\s*([^:\n]+?)(?=\s+|$)/i
  };

  // Extract matches with detailed logging
  const matches = {
    year,
    make,
    model,
    vin,
    roNumber,
    exteriorColor: cleanText.match(patterns.exteriorColor)?.[1] || '',
    interiorColor: cleanText.match(patterns.interiorColor)?.[1] || '',
    mileageIn: cleanText.match(patterns.mileageIn)?.[1]?.replace(/,/g, '') || '0',
    mileageOut: cleanText.match(patterns.mileageOut)?.[1]?.replace(/,/g, ''),
    license: cleanText.match(patterns.license)?.[1] || '',
    state: cleanText.match(patterns.state)?.[1] || '',
    productionDate: cleanText.match(patterns.productionDate)?.[1] || null,
    condition: cleanText.match(patterns.condition)?.[1] || null
  };

  log('Extracted field matches', { matches });

  // Build the final result
  const result = {
    data: {
      year: matches.year,
      make: matches.make,
      model: matches.model,
      vin: matches.vin,
      roNumber: matches.roNumber,
      color: {
        exterior: matches.exteriorColor,
        interior: matches.interiorColor
      },
      mileage: {
        in: parseInt(matches.mileageIn) || 0,
        out: matches.mileageOut ? parseInt(matches.mileageOut) : null
      },
      license: matches.license,
      state: matches.state,
      productionDate: matches.productionDate,
      condition: matches.condition
    },
    confidence: 0.9,  // Adjusted based on found fields
    missingFields: [],
    logs
  };

  if (!result.data.vin) {
    log('VIN extraction failed', { 
      textSample: cleanText.substring(0, 500),
      patterns: vinPatterns.map(p => p.toString())
    });
    throw new Error('No VIN found in the repair estimate. Please ensure the document contains a valid 17-character VIN number.');
  }

  return result;
};
